// Directional arrows over the field — the quick "which way is it coming from"
// read the crews currently open Windy for. A SYMBOL layer, not pixels baked into
// the field raster: the raster is a 512-px texture stretched across the whole
// mesh, and a glyph baked into it smears into mush at working zoom. Symbols are
// re-rasterized by the GPU per frame, so the arrows stay crisp under any zoom or
// bearing, and icon-rotation-alignment "map" keeps them pointing at the WATER's
// bearing rather than the screen's.
//
// The data is the mesh's optional direction planes (FROM convention, degrees) —
// arrows are drawn pointing in the TRAVEL direction (dir + 180), the same
// convention as the chart direction ribbons (see marks.ts dirArrows): an arrow
// shows where the wind is going, a barb would show where it is from, and this
// console draws arrows.
//
// Arrow POSITIONS are a decimated subset of mesh cells, re-chosen on moveend so
// the spacing stays roughly constant ON SCREEN (~52 px) as the map zooms. The
// stride is phase-locked to grid index 0, so panning shifts which cells are
// visible but never which cells are ELIGIBLE — arrows slide with the map instead
// of popping to new offsets on every gesture.

import type { Map as MlMap } from "maplibre-gl";
import type { GeoJSONSource } from "maplibre-gl";
import type { ExpressionSpecification } from "@maplibre/maplibre-gl-style-spec";
import type { FieldKind, FieldMesh } from "../forecast/fieldmesh.js";
import { leadFadeFrac } from "./windfield.js";

const SRC = "dirarrows";
const LAYER = "dirarrows";
const ICON = "dirarrow";
/** Target on-screen spacing between arrows, px. The decimation stride is the
 *  nearest whole number of mesh cells to this at the viewport centre. */
const TARGET_PX = 52;

// Same pattern as overlays.ts: read the palette live so the arrows follow the
// Day/Dusk/Night tables, with hard fallbacks for a layer built before CSS.
// --navy flips light↔dark across the tables and --icon-halo is the ground each
// table wants under a glyph, so the pair reads on the pale Day sea and the
// near-black Night one alike.
const CSS_FALLBACK: Record<string, string> = {
  "--navy": "#16232e",
  "--icon-halo": "#ffffff",
};
function cssVar(name: string): string {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || CSS_FALLBACK[name] || "#000000";
}

// --- the icon ----------------------------------------------------------------
// One SDF glyph, alpha = signed distance to an arrow pointing NORTH, computed
// analytically (a stroked shaft segment + a solid triangular head). SDF rather
// than a baked raster for two reasons: icon-color / icon-halo-color become paint
// properties, so a theme change is two setPaintProperty calls instead of the
// re-bake dance mapicons.ts needs; and the halo comes free, which is what keeps
// a 1.4-px line legible over the field's own greens and reds.

const ICON_PX = 48;
const SDF_RADIUS = 8; // px of distance ramp around the edge
const SDF_CUTOFF = 0.25; // MapLibre's glyph convention: edge lands at alpha 0.75

/** `icon-halo-width` for the whole layer, CSS px. The halo is what lifts a navy
 *  mark off a mid-tone field: 1.1 was enough over pale water and nothing over
 *  the Day ramp's greens, and 1.9 is what the arrow takes. It was briefly held
 *  down to 1.2 on a wave-specific glyph, whose two heads a thicker halo dilated
 *  into one another; there is no second glyph now, and no exception with it. */
export const ICON_HALO_PX = 1.9;

/** The icon-size range the layer draws the arrow at, low zoom to high. Sized to
 *  be READ, not merely present: at the original 0.55/0.8 the glyph was ~13 CSS
 *  px at working zoom and vanished into the field's greens in Day — navy on
 *  saturated green, at the size of a full stop. Arrow spacing is TARGET_PX
 *  (52 px), so a ~24 px glyph still sits in its own space with room around it. */
export const ICON_SIZE = { min: 0.85, max: 1.15 } as const;

/** Distance from p to segment ab, all in icon px. */
function segDist(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const vx = bx - ax;
  const vy = by - ay;
  const wx = px - ax;
  const wy = py - ay;
  const t = Math.max(0, Math.min(1, (wx * vx + wy * vy) / (vx * vx + vy * vy)));
  return Math.hypot(wx - t * vx, wy - t * vy);
}

/** A triangle as apex, base-left, base-right — the winding chart/marks.ts
 *  `arrowGlyph` returns, so the map and the canvas build one head the same way. */
type Tri = [[number, number], [number, number], [number, number]];

/** Signed distance from p to a triangle: negative inside, positive outside. The
 *  arrowhead is a triangle, so the icon is a union of one of these and a stroked
 *  shaft. */
function triSd(px: number, py: number, [a, b, c]: Tri): number {
  const d = Math.min(
    segDist(px, py, a[0], a[1], b[0], b[1]),
    segDist(px, py, b[0], b[1], c[0], c[1]),
    segDist(px, py, c[0], c[1], a[0], a[1]),
  );
  // Consistent winding: a point is inside when it sits on the same side of all
  // three edges (counter-clockwise here, y down).
  const side = (p: [number, number], q: [number, number]): number =>
    (q[0] - p[0]) * (py - p[1]) - (q[1] - p[1]) * (px - p[0]);
  const inside = side(a, b) <= 0 && side(b, c) <= 0 && side(c, a) <= 0;
  return inside ? -d : d;
}

/** SDF alpha for a signed distance, in MapLibre's glyph convention (edge at 0.75). */
function sdfAlpha(sd: number): number {
  return Math.round(255 * Math.max(0, Math.min(1, 1 - (sd / SDF_RADIUS + SDF_CUTOFF))));
}

/** Build the SDF arrow ImageData-shaped payload for map.addImage. */
function arrowSdf(): { width: number; height: number; data: Uint8Array } {
  const S = ICON_PX;
  // Geometry in icon px, y down, arrow pointing up (heading 0 = north).
  const cx = S * 0.5;
  const shaftTop = S * 0.36;
  const shaftBot = S * 0.8;
  const shaftR = S * 0.052;
  const tipY = S * 0.16;
  const headHalf = S * 0.19;
  const headBase = S * 0.44;
  // Head as a filled triangle (tip, base-left, base-right).
  const head: Tri = [
    [cx, tipY],
    [cx - headHalf, headBase],
    [cx + headHalf, headBase],
  ];

  const data = new Uint8Array(S * S * 4);
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const px = x + 0.5;
      const py = y + 0.5;
      // Shaft: stroked segment. Head: signed, negative inside.
      const dShaft = segDist(px, py, cx, shaftTop, cx, shaftBot) - shaftR;
      const sd = Math.min(dShaft, triSd(px, py, head)); // positive outside, negative inside
      data[(y * S + x) * 4 + 3] = sdfAlpha(sd); // SDF lives in alpha; rgb ignored
    }
  }
  return { width: S, height: S, data };
}

function ensureIcon(map: MlMap): void {
  if (!map.hasImage(ICON)) map.addImage(ICON, arrowSdf(), { sdf: true, pixelRatio: 2 });
}

// --- state -------------------------------------------------------------------

interface ArrowState {
  mesh: FieldMesh;
  /** Which DIRECTION plane draws — exactly one. The layer used to accept a
   *  "both" mode that interleaved wind and wave on a checkerboard of the same
   *  decimated grid, and it is gone: at working density the eye has to CLASSIFY
   *  every glyph before it can read anything, which is what forced the sea to
   *  carry a shape of its own, which is what nothing legible could be found for.
   *  One field at a time, chosen in map/windcontrol.ts and NAMED on the map. */
  kind: FieldKind;
  hour: number;
}
let state: ArrowState | null = null;
// The moveend re-decimation is this module's own subscription — the control that
// drives hours has no reason to know spacing is zoom-dependent. Kept per-map so
// clearArrows can unbind exactly what setArrows bound.
let boundMap: MlMap | null = null;
let onMove: (() => void) | null = null;

/** The direction/magnitude plane pair for an hour, or null when the mesh does not
 *  carry that direction plane (old server) — the feature-detect every caller
 *  relies on to behave exactly as before the planes existed. */
function planesFor(
  mesh: FieldMesh,
  kind: FieldKind,
  hour: number,
): { dir: Float32Array; mag: Float32Array } | null {
  const dirs = kind === "wave" ? mesh.waveDir : mesh.windDir;
  const mags = kind === "wave" ? mesh.wave : mesh.wind;
  const dir = dirs?.[hour];
  const mag = mags[hour];
  return dir !== undefined && mag !== undefined ? { dir, mag } : null;
}

/** Does this mesh carry directions for `kind` at all? The layer control uses it
 *  to decide whether the arrows toggle is worth showing. */
export function meshHasDir(mesh: FieldMesh, kind: FieldKind): boolean {
  return (kind === "wave" ? mesh.waveDir : mesh.windDir) !== undefined;
}

function buildFeatures(map: MlMap, s: ArrowState): GeoJSON.FeatureCollection {
  const empty: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [] };
  const { mesh } = s;
  if (mesh.nx < 2 || mesh.ny < 2) return empty;
  const planes = planesFor(mesh, s.kind, s.hour);
  if (planes === null) return empty;
  const dLon = (mesh.lonMax - mesh.lonMin) / (mesh.nx - 1);
  const dLat = (mesh.latMax - mesh.latMin) / (mesh.ny - 1);
  if (!(dLon > 0) || !(dLat > 0)) return empty;

  // Screen size of one cell at the viewport centre, so the stride tracks zoom.
  // Measured per axis: the cells are square in degrees, not on a Mercator screen,
  // and at high latitude a single shared stride would over-thin one axis.
  const c = map.getCenter();
  const p0 = map.project([c.lng, c.lat]);
  const px = map.project([c.lng + dLon, c.lat]);
  const py = map.project([c.lng, Math.min(85, c.lat + dLat)]);
  const cellPxX = Math.abs(px.x - p0.x);
  const cellPxY = Math.abs(py.y - p0.y);
  if (!(cellPxX > 0) || !(cellPxY > 0)) return empty;
  const strideX = Math.max(1, Math.round(TARGET_PX / cellPxX));
  const strideY = Math.max(1, Math.round(TARGET_PX / cellPxY));

  // Only cells in view (plus one stride of slack, so panning meets arrows rather
  // than blank water). MapLibre reports unwrapped longitudes after a trip around
  // the globe, so shift the view by whole worlds onto the mesh's frame first —
  // the source renders points on every world copy, so the features themselves
  // stay at mesh longitudes.
  const b = map.getBounds();
  const shift =
    360 * Math.round(((b.getWest() + b.getEast()) / 2 - (mesh.lonMin + mesh.lonMax) / 2) / 360);
  const west = b.getWest() - shift - dLon * strideX;
  const east = b.getEast() - shift + dLon * strideX;
  const south = b.getSouth() - dLat * strideY;
  const north = b.getNorth() + dLat * strideY;
  const ix0 = Math.max(0, Math.floor((west - mesh.lonMin) / dLon));
  const ix1 = Math.min(mesh.nx - 1, Math.ceil((east - mesh.lonMin) / dLon));
  const iy0 = Math.max(0, Math.floor((south - mesh.latMin) / dLat));
  const iy1 = Math.min(mesh.ny - 1, Math.ceil((north - mesh.latMin) / dLat));

  const features: GeoJSON.Feature<GeoJSON.Point, { rot: number }>[] = [];
  // Stride phase anchored at index 0 (not at ix0): the eligible subset is a
  // property of the GRID, so a pan re-crops it instead of re-choosing it.
  for (let iy = Math.ceil(iy0 / strideY) * strideY; iy <= iy1; iy += strideY) {
    for (let ix = Math.ceil(ix0 / strideX) * strideX; ix <= ix1; ix += strideX) {
      const i = iy * mesh.nx + ix;
      const dir = planes.dir[i];
      const mag = planes.mag[i];
      // Both must be present: a direction over land (or a magnitude with no
      // direction) draws nothing rather than something half-true.
      if (!Number.isFinite(dir) || !Number.isFinite(mag)) continue;
      features.push({
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [mesh.lonMin + ix * dLon, mesh.latMin + iy * dLat],
        },
        // FROM → travel: the glyph points where the wind/sea is GOING, matching
        // the chart ribbons' dirArrows convention. No per-feature glyph any
        // more — every arrow on the layer belongs to the same field, and which
        // field that is, is said in words on the map.
        properties: { rot: (dir + 180) % 360 },
      });
    }
  }
  return { type: "FeatureCollection", features };
}

function applyData(map: MlMap): void {
  if (state === null) return;
  const src = map.getSource(SRC);
  if (src !== undefined && "setData" in src) {
    (src as GeoJSONSource).setData(buildFeatures(map, state));
  }
}

function applyFade(map: MlMap): void {
  if (state === null || !map.getLayer(LAYER)) return;
  // In step with the raster's lead-time fade (same fraction, same horizon). The
  // raster mutes by SATURATION; a monochrome glyph has none to give, so opacity
  // carries the statement here — never to zero, for the same reason the raster
  // stays legible at the far end.
  map.setPaintProperty(LAYER, "icon-opacity", 1 - 0.55 * leadFadeFrac(state.mesh, state.hour));
}

/**
 * Install (or retarget) the arrow layer: `kind` names the ONE direction plane to
 * draw. The Workability raster is both fields, but its arrows are one of them,
 * chosen by the operator in the layer control and named on the map. A mesh
 * without that plane yields an empty layer, which is the required old-server
 * behaviour: no arrows, no errors.
 */
export function setArrows(map: MlMap, mesh: FieldMesh, kind: FieldKind, hourIdx: number): void {
  state = { mesh, kind, hour: hourIdx };
  ensureIcon(map);
  if (map.getSource(SRC) === undefined) {
    map.addSource(SRC, { type: "geojson", data: { type: "FeatureCollection", features: [] } });
  }
  if (!map.getLayer(LAYER)) {
    // Same insertion point as the field raster (see setField): under the reach
    // veil, so beyond-reach arrows recede with the field they annotate; added
    // after the raster, so they sit above the paint they read against.
    const beforeId = map.getLayer("reach-veil-fill")
      ? "reach-veil-fill"
      : map.getLayer("vessel-range-line")
        ? "vessel-range-line"
        : undefined;
    map.addLayer(
      {
        id: LAYER,
        type: "symbol",
        source: SRC,
        layout: {
          // One image and one size for the whole layer — every arrow on it is
          // the same field. This was a per-feature `case` picking a second glyph
          // for wave, at a size boosted a quarter to hold that glyph's weight
          // level with the arrow's; both went with the glyph.
          "icon-image": ICON,
          "icon-size": [
            "interpolate",
            ["linear"],
            ["zoom"],
            3,
            ICON_SIZE.min,
            8,
            ICON_SIZE.max,
          ] as ExpressionSpecification,
          "icon-rotate": ["get", "rot"] as ExpressionSpecification,
          // Rotation in MAP space and no collision culling: the arrows ARE the
          // grid — thinning is the decimation's job, and it must stay spatially
          // uniform, not fall to whichever glyph happened to tile first.
          "icon-rotation-alignment": "map",
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
        },
        paint: {
          "icon-color": cssVar("--navy"),
          "icon-halo-color": cssVar("--icon-halo"),
          // One width for the layer — see ICON_HALO_PX.
          "icon-halo-width": ICON_HALO_PX,
        },
      },
      beforeId,
    );
  }
  if (onMove === null) {
    // Re-decimate when the camera settles — zoom changes the px-per-cell the
    // stride is computed from, and a pan needs the crop re-cut. moveend fires
    // for both (a zoom gesture ends in one).
    onMove = () => {
      if (boundMap !== null) applyData(boundMap);
    };
    boundMap = map;
    map.on("moveend", onMove);
  }
  applyData(map);
  applyFade(map);
}

/** Step to hour `t` — the arrows follow the same displayed hour as the raster. */
export function showArrowHour(map: MlMap, t: number): void {
  if (state === null) return;
  state.hour = t;
  applyData(map);
  applyFade(map);
}

export function clearArrows(map: MlMap): void {
  if (map.getLayer(LAYER)) map.removeLayer(LAYER);
  if (map.getSource(SRC)) map.removeSource(SRC);
  if (onMove !== null && boundMap !== null) boundMap.off("moveend", onMove);
  onMove = null;
  boundMap = null;
  state = null;
}

/** Re-read the palette after a Day/Dusk/Night switch — called from the same
 *  refreshOverlayColors pass that re-tints every other CSS-var-coloured layer. */
export function refreshArrowColors(map: MlMap): void {
  if (!map.getLayer(LAYER)) return;
  map.setPaintProperty(LAYER, "icon-color", cssVar("--navy"));
  map.setPaintProperty(LAYER, "icon-halo-color", cssVar("--icon-halo"));
}
